// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import API from '../api/client';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();

  // 1️⃣ Estado de autenticación
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!localStorage.getItem('accessToken')
  );
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('user');
    return u ? JSON.parse(u) : null;
  });

  // Para saltarse la invalidación en el mount inicial
  const prevAuth = useRef(isAuthenticated);

  // 2️⃣ Efecto que vigila cambios de isAuthenticated
  useEffect(() => {
    // Si pasó de true → false (logout), invalidamos pets (noauth) y favorites
    if (prevAuth.current && !isAuthenticated) {
      queryClient.invalidateQueries({
        queryKey: ['pets'],
        exact: false,        // matchea cualquier ['pets', ...]
        refetchActive: true, // sólo refetch de la query activa (ahora “noauth”)
        refetchInactive: false
      });
      queryClient.invalidateQueries({ queryKey: ['favorites'], exact: false });
    }
    prevAuth.current = isAuthenticated;
  }, [isAuthenticated, queryClient]);

  // 3️⃣ login guarda token + userInfo
  const login = (token, userInfo = {}) => {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('user', JSON.stringify(userInfo));
    setIsAuthenticated(true);
    setUser(userInfo);
    API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  // Función para actualizar los datos del usuario que ya tiene el contexto
  const updateUser = (newUserData) => {
    setUser(prev => {
      const updated = { ...prev, ...newUserData };
      // Guardamos el usuario completo ya mergeado
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  // 4️⃣ logout sólo actualiza el estado / limpia storage
  const logout = () => {
    console.log('[AuthContext] 🔒 logout() llamado');

    // 👉 1) limpieza de sesiones
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    queryClient.clear();
    delete API.defaults.headers.common['Authorization'];
  };

  // 5️⃣ Rehidratar al arrancar
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const u     = localStorage.getItem('user');
    if (token && u) {
      setAccessToken(token);
      setUser(JSON.parse(u));
      setIsAuthenticated(true);
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      accessToken,
      isAuthenticated,
      login,
      logout,
      updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};